import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Link, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import {
  BottomSheet,
  DialogFooter,
  Illustration,
  SystemInlineBanner,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { parseISO } from "date-fns";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../../components/BottomSheetHeader";
import { Button } from "../../../components/Button";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { useWorkplaceModalsDataContext } from "../../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { MyShiftCard } from "../../Card/MyShiftCard";

export interface DoubleBookedSheetProps {
  modalState: UseModalState;
}

export function DoubleBookedSheet(props: DoubleBookedSheetProps) {
  const { modalState } = props;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const { shift, double, doubleBookingResults, isForYouShift, isTopThreeShift } =
    useShiftModalsDataContext();

  const isShiftBookedWithDouble =
    isDefined(shift) &&
    isDefined(doubleBookingResults) &&
    doubleBookingResults.bookedShiftIds.includes(shift.id);

  const logContext = {
    shiftId: shift?.id,
    workplaceId: double?.workplaceId,
    bookedShiftIds: doubleBookingResults?.bookedShiftIds ?? [],
  };

  useLogEffect(APP_V2_USER_EVENTS.FOR_YOU_SHIFTS_SHIFT_BOOKED, logContext, {
    enabled: modalState.modalIsOpen && isForYouShift && isShiftBookedWithDouble,
  });

  useLogEffect(APP_V2_USER_EVENTS.TOP_THREE_SHIFT_BOOKED, logContext, {
    enabled: modalState.modalIsOpen && isTopThreeShift && !isForYouShift && isShiftBookedWithDouble,
  });

  const workplace = shift?.workplace;
  const workplaceTimezone = workplace?.attributes?.location?.timezone;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace?.id,
  });

  const qualification = double?.qualification ?? "";
  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels(
    qualification ? [qualification] : []
  );
  const [qualificationText] = qualificationDisplayLabels;

  const failedCount = doubleBookingResults?.failedShiftIds?.length ?? 0;

  // Build ordered list of booked shifts (chronological)
  const bookedShifts = useMemo(() => {
    if (!shift || !double) {
      return [];
    }

    const bookedShiftIds = new Set(doubleBookingResults?.bookedShiftIds ?? []);

    const anchorShift = {
      id: shift.id,
      start: shift.attributes.start,
      end: shift.attributes.end,
      timeSlot: shift.attributes.timeSlot,
      pay: double.anchorShiftPay,
    };

    const companionShiftsData = double.companionShifts.map((companion) => ({
      id: companion.id,
      start: companion.attributes.start,
      end: companion.attributes.end,
      timeSlot: companion.attributes.timeSlot,
      pay: double.companionShiftPays?.get(companion.id),
    }));

    return [anchorShift, ...companionShiftsData]
      .filter((s) => bookedShiftIds.has(s.id))
      .sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());
  }, [shift, double, doubleBookingResults?.bookedShiftIds]);

  const bookedCount = bookedShifts.length;

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{ height: "100%" }}
      header={
        <BottomSheetHeader
          onClose={() => {
            modalState.closeModal();
          }}
        />
      }
      footer={
        isDefined(workplace) ? (
          <DialogFooter orientation="vertical" LinkComponent={Link}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                const modalPath = generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                  workplaceId: workplace.id,
                });
                modalState.closeModal();
                history.push(modalPath);
              }}
            >
              See workplace profile
            </Button>
          </DialogFooter>
        ) : undefined
      }
    >
      <DialogContent sx={{ flex: 1, minHeight: 0, overflowY: "auto", backgroundColor: "unset" }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          <Illustration type="shift-booked" />
          <Title variant="h2" component="h2" sx={{ textAlign: "center" }}>
            {bookedCount === 2 ? "Your double is booked!" : `${bookedCount} shift booked!`}
          </Title>

          {failedCount > 0 && (
            <SystemInlineBanner
              iconType="triangle-exclamation"
              color="warning"
              sx={{ width: "100%" }}
            >
              <Text inline variant="body2" color="inherit">
                1 shift in the double couldn&apos;t be confirmed
              </Text>
            </SystemInlineBanner>
          )}

          <Stack spacing={5} sx={{ width: "100%" }}>
            {bookedShifts.map((bookedShift) => (
              <MyShiftCard
                key={bookedShift.id}
                outlined
                shiftStart={bookedShift.start}
                shiftEnd={bookedShift.end}
                shiftTimeSlot={bookedShift.timeSlot}
                workplaceName={workplace?.attributes?.name}
                workplaceTimezone={workplaceTimezone}
                shiftFormattedTotalPay={
                  isDefined(bookedShift.pay?.totalPayInMinorUnits)
                    ? formatCentsAsUsd(bookedShift.pay.totalPayInMinorUnits)
                    : undefined
                }
                shiftTotalPay={
                  isDefined(bookedShift.pay?.totalPayInMinorUnits)
                    ? bookedShift.pay.totalPayInMinorUnits / 100
                    : undefined
                }
                shiftHourlyPay={
                  isDefined(bookedShift.pay?.hourlyPayInMinorUnits)
                    ? formatCentsAsUsd(bookedShift.pay.hourlyPayInMinorUnits)
                    : undefined
                }
                shiftBoost={undefined}
                requiresBreak={workplace?.attributes?.requiresLunchBreak}
                breakDurationInMinutes={mandatoryBreakDurationInMinutes}
                qualificationText={qualificationText ?? qualification}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
